import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { usePark } from "@/contexts/ParkContext";
import { PageHeader, formatCurrency, formatDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Plus, Search, UserPlus, SlidersHorizontal, ArrowUp, ArrowDown,
  Filter, Printer, Tag, X, Phone, MessageSquare, Mail,
} from "lucide-react";
import { toast } from "sonner";
import TenantSearchPanel from "@/components/TenantSearchPanel";

const exportUrl = (path, parkId, searchState) => {
  const url = new URL(`${API}${path}`);
  url.searchParams.set("park_id", parkId);
  if (searchState && Object.keys(searchState).length) {
    url.searchParams.set("search_state", JSON.stringify(searchState));
  }
  return url.toString();
};

// Map a column key to the backend search field it filters on.
// Address column renders combined address + city/state/zip as two lines to avoid horizontal scroll.
// City / State / Zip remain searchable via the Find panel — they're just not their own columns.
const COLUMNS = [
  { key: "name", label: "Name", searchKey: "name", sortGet: (t) => `${t.last_name || ""} ${t.first_name || ""}`.toLowerCase() },
  { key: "lot", label: "Lot", searchKey: "lot_number", sortGet: (t) => t._lot_number || "" },
  { key: "occ", label: "Occ", sortGet: (t) => t._occ_status || "", hideBelowLg: true },
  { key: "status", label: "Status", searchKey: "contract_status", sortGet: (t) => t._contract_status || "" },
  { key: "check_in", label: "Dates", sortGet: (t) => t._check_in || "" },
  { key: "term", label: "Term", searchKey: "lease_type", sortGet: (t) => t._lease_type || "", hideBelowLg: true },
  { key: "address", label: "Address", searchKey: "address", sortGet: (t) => (t.address || "").toLowerCase() },
  { key: "phone", label: "Phone", sortGet: (t) => t.cell_phone || "" },
  { key: "email", label: "Email", searchKey: "email", sortGet: (t) => (t.email || "").toLowerCase() },
  { key: "balance", label: "Balance", sortGet: (t) => t._balance || 0, align: "right" },
];

const STATUS_OPTIONS = ["Active", "Reservation", "Closed", "Cancelled"];
const TERM_OPTIONS = ["Short Term", "Long Term", "Annual", "Seasonal", "Sub Rental", "Group"];

export default function TenantsPage() {
  const { currentPark } = usePark();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [searchState, setSearchState] = useState({});
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", cell_phone: "", city: "", state: "" });
  const navigate = useNavigate();

  const fetchTenants = async (body = {}) => {
    setLoading(true);
    try {
      const { data } = await api.post("/tenants/search", body);
      setTenants(data.rows);
      const filterCount = Object.keys(body).length;
      if (filterCount > 0) toast.success(`${data.total} match${data.total === 1 ? "" : "es"}`);
    } catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (currentPark?.id) fetchTenants(searchState); /* eslint-disable-next-line */ }, [currentPark?.id]);

  const runPowerSearch = (body) => { setSearchState(body); setFindOpen(false); fetchTenants(body); };
  const clearAll = () => { setSearchState({}); fetchTenants({}); };

  const setColumnFilter = (key, value) => {
    const next = { ...searchState };
    if (value === "" || value === null || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    setSearchState(next);
    fetchTenants(next);
  };

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sort.key);
    if (!col) return tenants;
    const cp = [...tenants];
    cp.sort((a, b) => {
      const av = col.sortGet(a), bv = col.sortGet(b);
      if (typeof av === "number" && typeof bv === "number") return sort.dir === "asc" ? av - bv : bv - av;
      return sort.dir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return cp;
  }, [tenants, sort]);

  const toggleSort = (key) => {
    setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  const createTenant = async () => {
    if (!form.first_name || !form.last_name) { toast.error("First and last name are required"); return; }
    try {
      const { data } = await api.post("/tenants", form);
      toast.success("Tenant created");
      setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", cell_phone: "", city: "", state: "" });
      navigate(`/tenants/${data.id}`);
    } catch { toast.error("Failed to create"); }
  };

  const activeFilters = Object.keys(searchState);
  const parkId = currentPark?.id;

  return (
    <div data-testid="tenants-page">
      <PageHeader
        title="Tenants"
        subtitle={`${sorted.length} record${sorted.length === 1 ? "" : "s"}${activeFilters.length ? ` · ${activeFilters.length} filter${activeFilters.length === 1 ? "" : "s"} active` : ""}`}
        testId="tenants-header"
        actions={
          <div className="flex items-center gap-2">
            <a href={exportUrl("/tenant-reports/print-list.pdf", parkId, searchState)} target="_blank" rel="noreferrer" data-testid="print-list-link">
              <Button variant="outline" size="sm" className="gap-1.5 border-[#EAEAE5]"><Printer size={14} /> Print List</Button>
            </a>
            <a href={exportUrl("/tenant-reports/labels-avery-5160.pdf", parkId, searchState)} target="_blank" rel="noreferrer" data-testid="avery-labels-link">
              <Button variant="outline" size="sm" className="gap-1.5 border-[#EAEAE5]"><Tag size={14} /> Avery 5160</Button>
            </a>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white gap-1.5" data-testid="new-tenant-btn"><UserPlus size={14} /> New Tenant</Button>
              </DialogTrigger>
              <DialogContent className="!bg-white border-[#EAEAE5] max-w-[calc(100vw-2rem)] sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "Outfit" }}>New Tenant</DialogTitle>
                  <DialogDescription className="text-xs text-[#737A76]">Quick create — full details on the tenant page.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="new-tenant-first" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="new-tenant-last" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Cell</Label><Input value={form.cell_phone} onChange={(e) => setForm({ ...form, cell_phone: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={createTenant} data-testid="create-tenant-submit">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Button
          variant={findOpen ? "default" : "outline"}
          onClick={() => setFindOpen(!findOpen)}
          className={findOpen ? "bg-[#284B3B] hover:bg-[#1E3F32] text-white" : "border-[#EAEAE5]"}
          data-testid="tenant-find-toggle"
        >
          <SlidersHorizontal size={14} className="mr-1.5" /> Find
        </Button>
        {activeFilters.length > 0 && (
          <Button variant="ghost" onClick={clearAll} className="text-[#A13E34]" data-testid="tenant-clear-search">
            <X size={14} className="mr-1" /> Clear all filters
          </Button>
        )}
        {activeFilters.map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-xs bg-[#F0F0EC] text-[#284B3B] px-2 py-1 rounded-full" data-testid={`filter-chip-${k}`}>
            <span className="uppercase text-[10px] tracking-wider">{k.replace(/_/g, " ")}</span>
            <span className="font-medium">{String(searchState[k])}</span>
            <button onClick={() => setColumnFilter(k, "")} className="hover:text-[#A13E34]" aria-label={`Remove ${k} filter`}>
              <X size={11} />
            </button>
          </span>
        ))}
      </div>

      <TenantSearchPanel
        open={findOpen}
        onClose={() => setFindOpen(false)}
        onSearch={runPowerSearch}
        onClear={clearAll}
      />

      <Card className="border-[#EAEAE5] shadow-none overflow-hidden hidden md:block">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm pd-table whitespace-nowrap" data-testid="tenants-table">
            <thead>
              <tr className="text-left border-b border-[#EAEAE5] bg-[#FAFAF7]">
                {COLUMNS.map((c) => (
                  <ColumnHeader
                    key={c.key}
                    col={c}
                    sort={sort}
                    onSort={toggleSort}
                    searchState={searchState}
                    onApply={(value) => setColumnFilter(c.searchKey, value)}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-8 text-center text-[#737A76]" colSpan={COLUMNS.length}>Loading…</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td className="px-4 py-8 text-center text-[#737A76]" colSpan={COLUMNS.length}>No tenants found.</td></tr>
              ) : sorted.map((t) => (
                <tr
                  key={t.id}
                  className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF7] cursor-pointer"
                  onClick={() => navigate(`/tenants/${t.id}`)}
                  data-testid={`tenant-row-${t.id}`}
                >
                  <td className="px-2 py-2.5 font-medium">{t.last_name}{t.last_name && t.first_name ? ", " : ""}{t.first_name}</td>
                  <td className="px-2 tabular-nums">{t._lot_number || "—"}</td>
                  <td className="px-2 hidden 2xl:table-cell">
                    {t._occ_status === "In" ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#E3F0E6] text-[#297343]">In</span>
                      : t._occ_status === "Out" ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F0F0EC] text-[#737A76]">Out</span>
                      : <span className="text-[#737A76]">—</span>}
                  </td>
                  <td className="px-2 text-[#737A76]">{t._contract_status || "—"}</td>
                  <td className="px-2 text-[#737A76] tabular-nums">
                    <div className="leading-tight">
                      <div>{t._check_in ? formatDate(t._check_in) : "—"}</div>
                      <div className="text-[11px] text-[#9BA29D]">{t._check_out ? formatDate(t._check_out) : "—"}</div>
                    </div>
                  </td>
                  <td className="px-2 text-[#737A76] hidden 2xl:table-cell">{t._lease_type || "—"}</td>
                  <td className="px-2 text-[#737A76]">
                    <div className="leading-tight max-w-[180px]">
                      <div className="truncate" title={t.address || ""}>{t.address || "—"}</div>
                      {(t.city || t.state || t.zip_code) && (
                        <div className="text-[11px] text-[#9BA29D] truncate">
                          {[t.city, t.state].filter(Boolean).join(", ")}
                          {t.zip_code ? ` ${t.zip_code}` : ""}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-2 text-[#737A76] tabular-nums">{t.cell_phone || "—"}</td>
                  <td className="px-2 text-[#737A76]">
                    <div className="max-w-[180px] truncate" title={t.email || ""}>{t.email || "—"}</div>
                  </td>
                  <td className={`px-3 text-right tabular-nums ${t._balance > 0 ? "text-[#A13E34] font-medium" : "text-[#737A76]"}`}>
                    {t._balance > 0 ? formatCurrency(t._balance) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Mobile card list (≤ md) */}
      <div className="md:hidden space-y-2" data-testid="tenants-mobile-list">
        {loading ? (
          <div className="bg-white border border-[#EAEAE5] rounded-lg p-6 text-center text-[#737A76] text-sm">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="bg-white border border-[#EAEAE5] rounded-lg p-6 text-center text-[#737A76] text-sm">No tenants found.</div>
        ) : sorted.map((t) => (
          <TenantCard key={t.id} tenant={t} onOpen={() => navigate(`/tenants/${t.id}`)} />
        ))}
      </div>
    </div>
  );
}

/** Compact mobile tenant card with tap-to-call/text/email actions. */
function TenantCard({ tenant: t, onOpen }) {
  const fullName = `${t.last_name || ""}${t.last_name && t.first_name ? ", " : ""}${t.first_name || ""}`.trim() || "—";
  const phone = (t.cell_phone || "").trim();
  const telHref = phone ? `tel:${phone.replace(/[^\d+]/g, "")}` : null;
  const smsHref = phone ? `sms:${phone.replace(/[^\d+]/g, "")}` : null;
  const mailHref = t.email ? `mailto:${t.email}` : null;
  const occBadge = t._occ_status === "In"
    ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#E3F0E6] text-[#297343]">In</span>
    : t._occ_status === "Out"
      ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F0F0EC] text-[#737A76]">Out</span>
      : null;

  return (
    <div
      className="bg-white border border-[#EAEAE5] rounded-lg p-3 active:bg-[#FAFAF7]"
      data-testid={`tenant-card-${t.id}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left"
        data-testid={`tenant-card-open-${t.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-[#1A211D] truncate">{fullName}</div>
            <div className="text-xs text-[#737A76] mt-0.5 truncate">
              {t._lot_number ? <>Lot <span className="font-mono tabular-nums text-[#1A211D]">{t._lot_number}</span></> : "No lot"}
              {t._lease_type ? <> · {t._lease_type}</> : null}
              {t._contract_status ? <> · {t._contract_status}</> : null}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {occBadge}
            {t._balance > 0 && (
              <span className="text-xs font-medium tabular-nums text-[#A13E34]">
                {formatCurrency(t._balance)}
              </span>
            )}
          </div>
        </div>
      </button>

      {(telHref || smsHref || mailHref) && (
        <div className="mt-2.5 pt-2.5 border-t border-[#F0F0EC] flex gap-1.5">
          {telHref && (
            <a
              href={telHref}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 h-10 flex items-center justify-center gap-1.5 bg-[#E3F0E6] text-[#1F6336] rounded-md text-xs font-medium active:bg-[#D4E5D8]"
              data-testid={`tenant-card-call-${t.id}`}
            >
              <Phone size={14} /> Call
            </a>
          )}
          {smsHref && (
            <a
              href={smsHref}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 h-10 flex items-center justify-center gap-1.5 bg-[#FFF4D4] text-[#946A00] rounded-md text-xs font-medium active:bg-[#F4E4B5]"
              data-testid={`tenant-card-sms-${t.id}`}
            >
              <MessageSquare size={14} /> Text
            </a>
          )}
          {mailHref && (
            <a
              href={mailHref}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 h-10 flex items-center justify-center gap-1.5 bg-[#F0F0EC] text-[#284B3B] rounded-md text-xs font-medium active:bg-[#EAEAE5]"
              data-testid={`tenant-card-email-${t.id}`}
            >
              <Mail size={14} /> Email
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function ColumnHeader({ col, sort, onSort, searchState, onApply }) {
  const isSorted = sort.key === col.key;
  const isFiltered = col.searchKey && searchState[col.searchKey] != null && searchState[col.searchKey] !== "";
  const align = col.align === "right" ? "text-right" : "";

  return (
    <th className={`px-2 py-3 ${align} font-medium text-[10px] uppercase tracking-wider text-[#737A76] ${col.hideBelowLg ? "hidden 2xl:table-cell" : ""}`}>
      <div className="inline-flex items-center gap-1">
        <button
          onClick={() => onSort(col.key)}
          className={`inline-flex items-center gap-0.5 hover:text-[#1A211D] ${isSorted ? "text-[#284B3B]" : ""}`}
          data-testid={`th-${col.key}-sort`}
          title={`Sort by ${col.label}`}
        >
          {col.label}
          {isSorted && (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
        </button>
        {col.searchKey && (
          <ColumnFilterPopover
            col={col}
            currentValue={searchState[col.searchKey] || ""}
            onApply={onApply}
            isActive={isFiltered}
          />
        )}
      </div>
    </th>
  );
}

function ColumnFilterPopover({ col, currentValue, onApply, isActive }) {
  const [value, setValue] = useState(currentValue);
  const [open, setOpen] = useState(false);

  useEffect(() => { setValue(currentValue); }, [currentValue, open]);

  const apply = () => { onApply(value.trim()); setOpen(false); };
  const clear = () => { setValue(""); onApply(""); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`p-0.5 rounded hover:bg-[#F0F0EC] ${isActive ? "text-[#284B3B] bg-[#E3F0E6]" : "text-[#A8B0AB] hover:text-[#284B3B]"}`}
          data-testid={`th-${col.key}-filter-btn`}
          aria-label={`Filter by ${col.label}`}
          title={`Filter by ${col.label}${isActive ? ` · current: ${currentValue}` : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Filter size={11} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3 bg-white border-[#EAEAE5]" align="start">
        <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Filter — {col.label}</Label>
        {col.key === "status" ? (
          <select value={value} onChange={(e) => setValue(e.target.value)} className="w-full h-9 mt-1 border border-[#EAEAE5] rounded-md px-2 bg-white text-sm" data-testid={`th-${col.key}-filter-input`} autoFocus>
            <option value="">Any</option>
            {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : col.key === "term" ? (
          <select value={value} onChange={(e) => setValue(e.target.value)} className="w-full h-9 mt-1 border border-[#EAEAE5] rounded-md px-2 bg-white text-sm" data-testid={`th-${col.key}-filter-input`} autoFocus>
            <option value="">Any</option>
            {TERM_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
            placeholder={`Search ${col.label.toLowerCase()}…`}
            className="mt-1 h-9 bg-white border-[#EAEAE5]"
            data-testid={`th-${col.key}-filter-input`}
            autoFocus
          />
        )}
        <div className="flex items-center justify-end gap-1.5 mt-2">
          {isActive && (
            <Button size="sm" variant="ghost" onClick={clear} data-testid={`th-${col.key}-filter-clear`}>Clear</Button>
          )}
          <Button size="sm" className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={apply} data-testid={`th-${col.key}-filter-apply`}>Apply</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
